import type { Router } from "vue-router";

import { isNativePlatform } from "../capacitor/platform";
import { clearSessionToken } from "../capacitor/session-token";

import type { ApiError } from "./apiError";
import { setResponsePolicy } from "./http";

// markSessionSlotUnavailable and markPasswordChangeRequired are the user portal's alone.
export interface SessionControl {
  end: (options: { clearCache: boolean }) => void;
  markSessionSlotUnavailable?: () => void;
  markPasswordChangeRequired?: () => void;
}

export type RouteType = "admin" | "user" | "unknown";

export type PolicyAction =
  | { kind: "login"; sessionExpired: boolean }
  | { kind: "totp" }
  | { kind: "totpVerify" }
  | { kind: "sessionLimit" }
  | { kind: "passwordChange" }
  | { kind: "recovery" }
  | { kind: "pass" };

const AUTH_ROUTES: Record<"totp" | "totpVerify" | "login", Record<RouteType, string | null>> = {
  totp: { admin: "AdminTwoFactorOnboard", user: "TwoFactorOnboard", unknown: null },
  totpVerify: { admin: "AdminTwoFactor", user: "TwoFactor", unknown: null },
  login: { admin: "AdminLogin", user: "Login", unknown: null },
};

export function decide(
  status: number,
  code: string | undefined,
  routeType: RouteType,
  currentRouteName: unknown,
): PolicyAction {
  if (status === 401) {
    if (code === "err.invalidSession" || code === "err.unauthorised") {
      return { kind: "login", sessionExpired: routeType === "user" };
    }
    if (code === "err.totpEnrolmentRequired") return { kind: "totp" };
    if (code === "err.totpVerificationRequired") return { kind: "totpVerify" };
    if (code === "err.passwordChangeRequired" && routeType === "user" && currentRouteName !== "PasswordChange") {
      return { kind: "passwordChange" };
    }
  }
  if (
    status === 409 &&
    code === "err.sessionSlotRequired" &&
    routeType === "user" &&
    currentRouteName !== "SessionLimit"
  ) {
    return { kind: "sessionLimit" };
  }
  if (status === 503 && code === "err.databaseReadOnly") return { kind: "recovery" };
  return { kind: "pass" };
}

function routeTypeOf(router: Router): RouteType {
  const meta = router.currentRoute.value.meta;
  if (meta?.requiresAdminLogin) return "admin";
  if (meta?.requiresUserLogin) return "user";
  return "unknown";
}

// True when handled, NAVIGATING when leaving the page, false when nothing was done.
const NAVIGATING = Symbol("navigating");
type Outcome = boolean | typeof NAVIGATING;

export interface ResponsePolicyDeps {
  router: Router;
  session: SessionControl;
}

export function installResponsePolicy({ router, session }: ResponsePolicyDeps): void {
  // Pushing again during an auth redirect restarts the navigation, which 401s and loops.
  let authRedirectInFlight = false;

  async function redirect(routeName: string | null, sessionExpired = false): Promise<Outcome> {
    if (!routeName) return false;
    if (authRedirectInFlight) return true;
    authRedirectInFlight = true;
    try {
      await router.push({ name: routeName, query: sessionExpired ? { sessionExpired: "1" } : undefined });
    } finally {
      authRedirectInFlight = false;
    }
    return true;
  }

  async function act(status: number, code: string | undefined): Promise<Outcome> {
    const routeType = routeTypeOf(router);
    const action = decide(status, code, routeType, router.currentRoute.value.name);
    switch (action.kind) {
      case "pass":
        return false;
      case "login":
        // End the session first or the guard trusts the stale user and loops. On a
        // public page a cache clear would leave the page's own queries pending.
        session.end({ clearCache: Boolean(AUTH_ROUTES.login[routeType]) });
        await clearSessionToken();
        return redirect(AUTH_ROUTES.login[routeType], action.sessionExpired);
      case "totp":
        return redirect(AUTH_ROUTES.totp[routeType]);
      case "totpVerify":
        return redirect(AUTH_ROUTES.totpVerify[routeType]);
      case "sessionLimit":
        // The route guard holds SessionLimit only while HasSessionSlot === false.
        session.markSessionSlotUnavailable?.();
        return redirect("SessionLimit");
      case "passwordChange":
        // The route guard holds PasswordChange only while PasswordChangeRequired is set.
        session.markPasswordChangeRequired?.();
        return redirect("PasswordChange");
      case "recovery":
        // Server-rendered, so the cached SPA can't loop back here. Native has no such page.
        if (isNativePlatform()) void router.push({ name: "Login" });
        else window.location.href = "/_recovery";
        return NAVIGATING;
    }
  }

  setResponsePolicy(async (error: ApiError) => {
    const outcome = await act(error.status, error.code);
    if (outcome === NAVIGATING) return new Promise<never>(() => {});
    if (outcome) {
      error.handled = true;
      throw error;
    }
  });
}
